import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  Plus, 
  ArrowLeft,
  MoreVertical,
  Pencil,
  Trash2,
  MapPin,
  Building2,
  Calendar,
  DollarSign,
  Users,
  Package,
  FileText,
  CreditCard,
  CheckCircle,
  Clock,
  Target,
  TrendingUp,
  Search,
  LayoutGrid,
  List,
  Download,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  quote: 'bg-gray-50 text-gray-700 border-gray-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  on_hold: 'bg-orange-50 text-orange-700 border-orange-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-50 text-slate-700 border-slate-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  draft: 'bg-gray-50 text-gray-700 border-gray-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  partial: 'bg-orange-50 text-orange-700 border-orange-200',
  received: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const JobDetail = () => {
  const { t, i18n } = useTranslation();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Sites list controls
  const [siteSearch, setSiteSearch] = useState('');
  const [siteViewMode, setSiteViewMode] = useState('grid'); // 'grid' or 'list'
  const [sitePage, setSitePage] = useState(1);
  const sitesPerPage = 12;
  
  // Dialogs
  const [showScopeDialog, setShowScopeDialog] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showPODialog, setShowPODialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  
  // Form data
  const [scopeForm, setScopeForm] = useState({});
  const [teamForm, setTeamForm] = useState({});
  const [poForm, setPOForm] = useState({ items: [{}] });
  const [invoiceForm, setInvoiceForm] = useState({});
  const [paymentForm, setPaymentForm] = useState({});
  const [editingScope, setEditingScope] = useState(null);
  const [siteForm, setSiteForm] = useState({});

  // Filtered and paginated sites
  const filteredSites = useMemo(() => {
    if (!siteSearch) return sites;
    const search = siteSearch.toLowerCase();
    return sites.filter(s => 
      s.name?.toLowerCase().includes(search) ||
      s.site_number?.toLowerCase().includes(search) ||
      s.client_name?.toLowerCase().includes(search) ||
      s.location?.city?.toLowerCase().includes(search)
    );
  }, [sites, siteSearch]);

  const totalSitePages = Math.ceil(filteredSites.length / sitesPerPage);
  const paginatedSites = useMemo(() => {
    const start = (sitePage - 1) * sitesPerPage;
    return filteredSites.slice(start, start + sitesPerPage);
  }, [filteredSites, sitePage]);

  // Export sites to CSV
  const exportSitesToCSV = () => {
    const headers = ['Site Number', 'Site Name', 'Client', 'City', 'Contact Person', 'Phone', 'Status', 'Progress'];
    const rows = filteredSites.map(site => [
      site.site_number,
      site.name,
      site.client_name || '',
      site.location?.city || '',
      site.contact_person || '',
      site.contact_phone || '',
      site.status,
      `${site.workflow?.overall_progress || 0}%`
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${job?.project_name || 'project'}_sites_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    toast.success(isRTL ? 'تم تصدير الملف' : 'Sites exported to CSV');
  };

  const statusLabels = {
    quote: t('jobManagement.jobStatus.quote'),
    approved: t('jobManagement.jobStatus.approved'),
    in_progress: t('jobManagement.jobStatus.inProgress'),
    on_hold: t('jobManagement.jobStatus.onHold'),
    completed: t('jobManagement.jobStatus.completed'),
    closed: t('jobManagement.jobStatus.closed'),
    cancelled: t('jobManagement.jobStatus.cancelled'),
    pending: t('jobManagement.scopeStatus.pending'),
    draft: t('pages.payroll.draft'),
    sent: t('jobManagement.poStatus.sent'),
    partial: t('jobManagement.poStatus.partial'),
    received: t('jobManagement.poStatus.received'),
    paid: t('pages.payroll.paid')
  };

  useEffect(() => {
    fetchJob();
    fetchEmployees();
    fetchSuppliers();
    fetchProducts();
    fetchClients();
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      fetchSites();
    }
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/${jobId}`);
      setJob(res.data);
    } catch (err) {
      toast.error('Failed to load job');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/sites?project_id=${jobId}`);
      setSites(res.data);
    } catch (err) {
      console.error('Failed to load sites');
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/clients`);
      setClients(res.data);
    } catch (err) {
      console.error('Failed to fetch clients', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data.filter(e => e.status === 'active'));
    } catch (err) {
      console.error('Failed to fetch employees', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      console.error('Failed to fetch suppliers', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/products`);
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to fetch products', err);
    }
  };

  // Sites
  const handleSaveSite = async () => {
    try {
      const payload = {
        ...siteForm,
        project_id: jobId,
        location: {
          address: siteForm.address || '',
          city: siteForm.city || '',
          country: siteForm.country || 'Saudi Arabia'
        }
      };
      await axios.post(`${API_URL}/api/jobs/sites`, payload);
      toast.success(t('jobManagement.siteCreated'));
      setShowSiteDialog(false);
      setSiteForm({});
      fetchSites();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : t('jobManagement.failedToSave'));
      toast.error(errorMessage);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (window.confirm(t('jobManagement.confirmDeleteSite'))) {
      try {
        await axios.delete(`${API_URL}/api/jobs/sites/${siteId}`);
        toast.success(t('jobManagement.siteDeleted'));
        fetchSites();
      } catch (err) {
        toast.error(t('jobManagement.failedToDelete'));
      }
    }
  };

  // Status Update
  const handleUpdateStatus = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/jobs/${jobId}`, { status: newStatus });
      toast.success(t('jobManagement.statusUpdated'));
      fetchJob();
      setShowStatusDialog(false);
    } catch (err) {
      toast.error(t('jobManagement.failedToUpdate'));
    }
  };

  // Scopes
  const handleSaveScope = async () => {
    try {
      if (editingScope) {
        await axios.put(`${API_URL}/api/jobs/${jobId}/scopes/${editingScope.id}`, scopeForm);
        toast.success(t('jobManagement.scopeUpdated'));
      } else {
        await axios.post(`${API_URL}/api/jobs/${jobId}/scopes`, scopeForm);
        toast.success(t('jobManagement.scopeAdded'));
      }
      setShowScopeDialog(false);
      setScopeForm({});
      setEditingScope(null);
      fetchJob();
    } catch (err) {
      toast.error(t('jobManagement.failedToSave'));
    }
  };

  const handleDeleteScope = async (scopeId) => {
    if (window.confirm(t('jobManagement.confirmDeleteScope'))) {
      try {
        await axios.delete(`${API_URL}/api/jobs/${jobId}/scopes/${scopeId}`);
        toast.success(t('jobManagement.scopeDeleted'));
        fetchJob();
      } catch (err) {
        toast.error(t('jobManagement.failedToDelete'));
      }
    }
  };

  // Team
  const handleAssignTeam = async () => {
    try {
      await axios.post(`${API_URL}/api/jobs/${jobId}/team`, teamForm);
      toast.success(t('jobManagement.teamAssigned'));
      setShowTeamDialog(false);
      setTeamForm({});
      fetchJob();
    } catch (err) {
      toast.error(t('jobManagement.failedToSave'));
    }
  };

  // PO
  const handleCreatePO = async () => {
    try {
      const items = poForm.items.filter(i => i.product_name && i.quantity);
      await axios.post(`${API_URL}/api/jobs/${jobId}/pos`, { ...poForm, items });
      toast.success(t('jobManagement.poCreated'));
      setShowPODialog(false);
      setPOForm({ items: [{}] });
      fetchJob();
    } catch (err) {
      toast.error(t('jobManagement.failedToSave'));
    }
  };

  // Invoice
  const handleCreateInvoice = async () => {
    try {
      await axios.post(`${API_URL}/api/jobs/${jobId}/invoices`, invoiceForm);
      toast.success(t('jobManagement.invoiceCreated'));
      setShowInvoiceDialog(false);
      setInvoiceForm({});
      fetchJob();
    } catch (err) {
      toast.error(t('jobManagement.failedToSave'));
    }
  };

  // Payment
  const handleRecordPayment = async () => {
    try {
      await axios.post(`${API_URL}/api/jobs/${jobId}/payments`, paymentForm);
      toast.success(t('jobManagement.paymentRecorded'));
      setShowPaymentDialog(false);
      setPaymentForm({});
      fetchJob();
    } catch (err) {
      toast.error(t('jobManagement.failedToSave'));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="h-8 w-8 animate-spin" /></div>;
  if (!job) return null;

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h1 className="text-2xl font-bold">{job.job_number}</h1>
              <Badge className={statusColors[job.status]}>{statusLabels[job.status]}</Badge>
            </div>
            <p className="text-muted-foreground">{job.title}</p>
          </div>
        </div>
        <Button onClick={() => setShowStatusDialog(true)}>{t('jobManagement.updateStatus')}</Button>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Building2 className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('jobManagement.client')}</p>
                <p className="font-semibold">{job.client_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <MapPin className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('jobManagement.site')}</p>
                <p className="font-semibold">{job.site_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Target className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('jobManagement.progress')}</p>
                <div className="flex items-center gap-2">
                  <Progress value={job.overall_progress || 0} className="w-20 h-2" />
                  <span className="font-semibold">{job.overall_progress || 0}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('jobManagement.value')}</p>
                <p className="font-semibold">{currencySymbol} {(job.estimated_value || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary */}
      <Card>
        <CardHeader><CardTitle>{t('jobManagement.financialSummary')}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-6 gap-4 text-center">
            <div><p className="text-2xl font-bold">{currencySymbol} {(job.total_material_cost || 0).toLocaleString()}</p><p className="text-sm text-muted-foreground">{t('jobManagement.materialCost')}</p></div>
            <div><p className="text-2xl font-bold">{currencySymbol} {(job.total_labor_cost || 0).toLocaleString()}</p><p className="text-sm text-muted-foreground">{t('jobManagement.laborCost')}</p></div>
            <div><p className="text-2xl font-bold">{currencySymbol} {(job.total_po_value || 0).toLocaleString()}</p><p className="text-sm text-muted-foreground">{t('jobManagement.poValue')}</p></div>
            <div><p className="text-2xl font-bold">{currencySymbol} {(job.total_invoiced || 0).toLocaleString()}</p><p className="text-sm text-muted-foreground">{t('jobManagement.invoiced')}</p></div>
            <div><p className="text-2xl font-bold text-emerald-600">{currencySymbol} {(job.total_paid || 0).toLocaleString()}</p><p className="text-sm text-muted-foreground">{t('jobManagement.paid')}</p></div>
            <div><p className="text-2xl font-bold text-red-600">{currencySymbol} {(job.balance_due || 0).toLocaleString()}</p><p className="text-sm text-muted-foreground">{t('jobManagement.balanceDue')}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="sites">
        <TabsList>
          <TabsTrigger value="sites" className="gap-2"><MapPin className="h-4 w-4" /> {t('jobManagement.sites')} ({sites?.length || 0})</TabsTrigger>
          <TabsTrigger value="scopes" className="gap-2"><Target className="h-4 w-4" /> {t('jobManagement.scopes')} ({job.scopes?.length || 0})</TabsTrigger>
          <TabsTrigger value="team" className="gap-2"><Users className="h-4 w-4" /> {t('jobManagement.team')} ({job.team_assignments?.length || 0})</TabsTrigger>
          <TabsTrigger value="pos" className="gap-2"><Package className="h-4 w-4" /> {t('jobManagement.purchaseOrders')} ({job.purchase_orders?.length || 0})</TabsTrigger>
          <TabsTrigger value="invoices" className="gap-2"><FileText className="h-4 w-4" /> {t('jobManagement.invoices')} ({job.invoices?.length || 0})</TabsTrigger>
          <TabsTrigger value="payments" className="gap-2"><CreditCard className="h-4 w-4" /> {t('jobManagement.payments')} ({job.payments?.length || 0})</TabsTrigger>
        </TabsList>

        {/* Sites Tab */}
        <TabsContent value="sites" className="mt-4">
          {/* Header with Search, View Toggle, Export */}
          <div className={`flex flex-col gap-4 mb-4`}>
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-semibold">{t('jobManagement.projectSites')}</h3>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button size="sm" variant="outline" onClick={exportSitesToCSV}>
                  <Download className="h-4 w-4 mr-1" /> CSV
                </Button>
                <Button size="sm" onClick={() => { setSiteForm({}); setShowSiteDialog(true); }}>
                  <Plus className="h-4 w-4 mr-1" /> {t('jobManagement.addSite')}
                </Button>
              </div>
            </div>
            
            {/* Search and View Toggle Row */}
            {sites.length > 0 && (
              <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="relative flex-1 max-w-sm">
                  <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                  <Input 
                    className={isRTL ? 'pr-10' : 'pl-10'} 
                    placeholder={isRTL ? 'بحث في المواقع...' : 'Search sites...'} 
                    value={siteSearch} 
                    onChange={e => { setSiteSearch(e.target.value); setSitePage(1); }} 
                  />
                </div>
                <div className={`flex items-center border rounded-md ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Button 
                    variant={siteViewMode === 'grid' ? 'secondary' : 'ghost'} 
                    size="sm" 
                    className="rounded-r-none"
                    onClick={() => setSiteViewMode('grid')}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant={siteViewMode === 'list' ? 'secondary' : 'ghost'} 
                    size="sm" 
                    className="rounded-l-none"
                    onClick={() => setSiteViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                <span className="text-sm text-muted-foreground">
                  {filteredSites.length} {isRTL ? 'موقع' : 'sites'}
                </span>
              </div>
            )}
          </div>

          {/* Grid View */}
          {siteViewMode === 'grid' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedSites?.map(site => (
                <Card key={site.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/projects/sites/${site.id}`)}>
                  <CardHeader className={`flex flex-row items-start justify-between pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        {site.name}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">{site.site_number}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/projects/sites/${site.id}`); }}>
                          <MapPin className="h-4 w-4 mr-2" /> {t('jobManagement.viewSite')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteSite(site.id); }}>
                          <Trash2 className="h-4 w-4 mr-2" /> {t('pages.common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Building2 className="h-4 w-4" />
                        <span>{site.client_name || '-'}</span>
                      </div>
                      {site.location?.city && (
                        <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <MapPin className="h-4 w-4" />
                          <span>{site.location.address ? `${site.location.address}, ` : ''}{site.location.city}</span>
                        </div>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 pt-3 border-t">
                      <div className={`flex justify-between text-xs mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-muted-foreground">{isRTL ? 'التقدم' : 'Progress'}</span>
                        <span className="font-medium">{site.workflow?.overall_progress || 0}%</span>
                      </div>
                      <Progress value={site.workflow?.overall_progress || 0} className="h-1.5" />
                    </div>
                    <div className={`flex justify-between mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Badge variant="outline" className={statusColors[site.status] || statusColors.active}>
                        {statusLabels[site.status] || site.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* List View */}
          {siteViewMode === 'list' && (
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className={`p-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'رقم الموقع' : 'Site #'}</th>
                      <th className={`p-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'الاسم' : 'Name'}</th>
                      <th className={`p-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'العميل' : 'Client'}</th>
                      <th className={`p-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'المدينة' : 'City'}</th>
                      <th className={`p-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'التقدم' : 'Progress'}</th>
                      <th className={`p-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'الحالة' : 'Status'}</th>
                      <th className="p-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSites?.map(site => (
                      <tr key={site.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/projects/sites/${site.id}`)}>
                        <td className="p-3 font-mono text-sm">{site.site_number}</td>
                        <td className="p-3 font-medium">{site.name}</td>
                        <td className="p-3 text-muted-foreground">{site.client_name || '-'}</td>
                        <td className="p-3 text-muted-foreground">{site.location?.city || '-'}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Progress value={site.workflow?.overall_progress || 0} className="h-1.5 w-16" />
                            <span className="text-xs">{site.workflow?.overall_progress || 0}%</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={statusColors[site.status] || statusColors.active}>
                            {statusLabels[site.status] || site.status}
                          </Badge>
                        </td>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/projects/sites/${site.id}`)}>
                                <MapPin className="h-4 w-4 mr-2" /> {t('jobManagement.viewSite')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteSite(site.id)}>
                                <Trash2 className="h-4 w-4 mr-2" /> {t('pages.common.delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {totalSitePages > 1 && (
            <div className={`flex items-center justify-center gap-2 mt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSitePage(p => Math.max(1, p - 1))}
                disabled={sitePage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {isRTL ? `${totalSitePages} من ${sitePage}` : `${sitePage} of ${totalSitePages}`}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSitePage(p => Math.min(totalSitePages, p + 1))}
                disabled={sitePage === totalSitePages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Empty State */}
          {(!sites || sites.length === 0) && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('jobManagement.noSitesInProject')}</p>
                <Button variant="outline" className="mt-3" onClick={() => { setSiteForm({}); setShowSiteDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> {t('jobManagement.addFirstSite')}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Scopes Tab */}
        <TabsContent value="scopes" className="mt-4">
          <div className={`flex justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-semibold">{t('jobManagement.workScopes')}</h3>
            <Button size="sm" onClick={() => { setScopeForm({}); setEditingScope(null); setShowScopeDialog(true); }}><Plus className="h-4 w-4 mr-1" /> {t('jobManagement.addScope')}</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {job.scopes?.map(scope => (
              <Card key={scope.id}>
                <CardHeader className={`flex flex-row items-center justify-between pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CardTitle className="text-base">{scope.name}</CardTitle>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Badge className={statusColors[scope.status]}>{statusLabels[scope.status]}</Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => { setEditingScope(scope); setScopeForm(scope); setShowScopeDialog(true); }}><Pencil className="h-4 w-4 mr-2" /> {t('pages.common.edit')}</DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteScope(scope.id)}><Trash2 className="h-4 w-4 mr-2" /> {t('pages.common.delete')}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">{scope.description || t('jobManagement.noDescription')}</p>
                  <div className="space-y-2">
                    <div className={`flex justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span>{t('jobManagement.progress')}</span>
                      <span>{scope.completion_percentage || 0}%</span>
                    </div>
                    <Progress value={scope.completion_percentage || 0} className="h-2" />
                  </div>
                  <div className={`flex justify-between mt-3 pt-3 border-t text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="text-muted-foreground">{t('jobManagement.estimated')}: {currencySymbol} {(scope.estimated_cost || 0).toLocaleString()}</span>
                    <span className="font-medium">{t('jobManagement.actual')}: {currencySymbol} {(scope.actual_cost || 0).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {(!job.scopes || job.scopes.length === 0) && (
              <Card className="col-span-2"><CardContent className="py-8 text-center text-muted-foreground">{t('jobManagement.noScopes')}</CardContent></Card>
            )}
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="mt-4">
          <div className={`flex justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-semibold">{t('jobManagement.teamAssignments')}</h3>
            <Button size="sm" onClick={() => { setTeamForm({}); setShowTeamDialog(true); }}><Plus className="h-4 w-4 mr-1" /> {t('jobManagement.assignMember')}</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('jobManagement.employee')}</TableHead>
                <TableHead>{t('jobManagement.role')}</TableHead>
                <TableHead>{t('jobManagement.scope')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.hours')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.rate')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.total')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.team_assignments?.map(member => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">{member.employee_name}</TableCell>
                  <TableCell>{member.role || '-'}</TableCell>
                  <TableCell>{job.scopes?.find(s => s.id === member.scope_id)?.name || t('jobManagement.allScopes')}</TableCell>
                  <TableCell className="text-right font-mono">{member.hours_worked || 0}h</TableCell>
                  <TableCell className="text-right font-mono">${member.hourly_rate || 0}/h</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{currencySymbol} {(member.total_cost || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {(!job.team_assignments || job.team_assignments.length === 0) && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{t('jobManagement.noTeam')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>

        {/* POs Tab */}
        <TabsContent value="pos" className="mt-4">
          <div className={`flex justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-semibold">{t('jobManagement.purchaseOrders')}</h3>
            <Button size="sm" onClick={() => { setPOForm({ items: [{}] }); setShowPODialog(true); }}><Plus className="h-4 w-4 mr-1" /> {t('jobManagement.createPO')}</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('jobManagement.poNumber')}</TableHead>
                <TableHead>{t('jobManagement.supplier')}</TableHead>
                <TableHead>{t('jobManagement.scope')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.total')}</TableHead>
                <TableHead>{t('jobManagement.statusLabel')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.pos_data?.map(po => (
                <TableRow key={po.id}>
                  <TableCell className="font-mono font-medium">{po.po_number}</TableCell>
                  <TableCell>{po.supplier_name}</TableCell>
                  <TableCell>{job.scopes?.find(s => s.id === po.scope_id)?.name || '-'}</TableCell>
                  <TableCell className="text-right font-mono">{currencySymbol} {(po.total || 0).toLocaleString()}</TableCell>
                  <TableCell><Badge className={statusColors[po.status]}>{statusLabels[po.status]}</Badge></TableCell>
                </TableRow>
              ))}
              {(!job.pos_data || job.pos_data.length === 0) && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t('jobManagement.noPOs')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <div className={`flex justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-semibold">{t('jobManagement.invoices')}</h3>
            <Button size="sm" onClick={() => { setInvoiceForm({}); setShowInvoiceDialog(true); }}><Plus className="h-4 w-4 mr-1" /> {t('jobManagement.createInvoice')}</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('jobManagement.invoiceNumber')}</TableHead>
                <TableHead>{t('jobManagement.date')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.total')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.paid')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.balance')}</TableHead>
                <TableHead>{t('jobManagement.statusLabel')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.invoices_data?.map(inv => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono font-medium">{inv.invoice_number}</TableCell>
                  <TableCell>{inv.issue_date}</TableCell>
                  <TableCell className="text-right font-mono">{currencySymbol} {(inv.total || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-600">{currencySymbol} {(inv.amount_paid || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-red-600">{currencySymbol} {(inv.balance_due || 0).toLocaleString()}</TableCell>
                  <TableCell><Badge className={statusColors[inv.status]}>{statusLabels[inv.status]}</Badge></TableCell>
                </TableRow>
              ))}
              {(!job.invoices_data || job.invoices_data.length === 0) && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{t('jobManagement.noInvoices')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="mt-4">
          <div className={`flex justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-semibold">{t('jobManagement.payments')}</h3>
            <Button size="sm" onClick={() => { setPaymentForm({ payment_date: new Date().toISOString().split('T')[0] }); setShowPaymentDialog(true); }}><Plus className="h-4 w-4 mr-1" /> {t('jobManagement.recordPayment')}</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('jobManagement.date')}</TableHead>
                <TableHead>{t('jobManagement.type')}</TableHead>
                <TableHead>{t('jobManagement.method')}</TableHead>
                <TableHead>{t('jobManagement.reference')}</TableHead>
                <TableHead className="text-right">{t('jobManagement.amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.payments_data?.map(payment => (
                <TableRow key={payment.id}>
                  <TableCell>{payment.payment_date}</TableCell>
                  <TableCell><Badge variant="outline">{t(`jobManagement.paymentType.${payment.payment_type}`)}</Badge></TableCell>
                  <TableCell>{payment.payment_method}</TableCell>
                  <TableCell className="font-mono">{payment.reference || '-'}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-emerald-600">{currencySymbol} {(payment.amount || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {(!job.payments_data || job.payments_data.length === 0) && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t('jobManagement.noPayments')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>

      {/* Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('jobManagement.updateStatus')}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-4">
            {['quote', 'approved', 'in_progress', 'on_hold', 'completed', 'closed'].map(status => (
              <Button key={status} variant={job.status === status ? 'default' : 'outline'} onClick={() => handleUpdateStatus(status)} className="justify-start">
                {statusLabels[status]}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Scope Dialog */}
      <Dialog open={showScopeDialog} onOpenChange={setShowScopeDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingScope ? t('jobManagement.editScope') : t('jobManagement.addScope')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('jobManagement.scopeName')} *</Label>
              <Input value={scopeForm.name || ''} onChange={e => setScopeForm({...scopeForm, name: e.target.value})} placeholder="Network Cabling" />
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.description')}</Label>
              <Textarea value={scopeForm.description || ''} onChange={e => setScopeForm({...scopeForm, description: e.target.value})} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.estimatedCost')}</Label>
                <Input type="number" value={scopeForm.estimated_cost || ''} onChange={e => setScopeForm({...scopeForm, estimated_cost: parseFloat(e.target.value) || 0})} />
              </div>
              {editingScope && (
                <div className="space-y-2">
                  <Label>{t('jobManagement.completion')} %</Label>
                  <Input type="number" min="0" max="100" value={scopeForm.completion_percentage || ''} onChange={e => setScopeForm({...scopeForm, completion_percentage: parseInt(e.target.value) || 0})} />
                </div>
              )}
            </div>
            {editingScope && (
              <div className="space-y-2">
                <Label>{t('jobManagement.statusLabel')}</Label>
                <Select value={scopeForm.status || 'pending'} onValueChange={v => setScopeForm({...scopeForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('jobManagement.scopeStatus.pending')}</SelectItem>
                    <SelectItem value="in_progress">{t('jobManagement.scopeStatus.inProgress')}</SelectItem>
                    <SelectItem value="completed">{t('jobManagement.scopeStatus.completed')}</SelectItem>
                    <SelectItem value="on_hold">{t('jobManagement.scopeStatus.onHold')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScopeDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveScope}>{editingScope ? t('pages.common.edit') : t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('jobManagement.assignMember')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('jobManagement.employee')} *</Label>
              <Select value={teamForm.employee_id || ''} onValueChange={v => setTeamForm({...teamForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder={t('jobManagement.selectEmployee')} /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.scope')}</Label>
              <Select value={teamForm.scope_id || 'all'} onValueChange={v => setTeamForm({...teamForm, scope_id: v === 'all' ? null : v})}>
                <SelectTrigger><SelectValue placeholder={t('jobManagement.allScopes')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('jobManagement.allScopes')}</SelectItem>
                  {job.scopes?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.role')}</Label>
                <Input value={teamForm.role || ''} onChange={e => setTeamForm({...teamForm, role: e.target.value})} placeholder="Technician" />
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.hourlyRate')}</Label>
                <Input type="number" value={teamForm.hourly_rate || ''} onChange={e => setTeamForm({...teamForm, hourly_rate: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAssignTeam}>{t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO Dialog */}
      <Dialog open={showPODialog} onOpenChange={setShowPODialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t('jobManagement.createPO')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.supplier')} *</Label>
                <Select value={poForm.supplier_id || ''} onValueChange={v => setPOForm({...poForm, supplier_id: v})}>
                  <SelectTrigger><SelectValue placeholder={t('jobManagement.selectSupplier')} /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.scope')}</Label>
                <Select value={poForm.scope_id || 'all'} onValueChange={v => setPOForm({...poForm, scope_id: v === 'all' ? null : v})}>
                  <SelectTrigger><SelectValue placeholder={t('jobManagement.allScopes')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('jobManagement.allScopes')}</SelectItem>
                    {job.scopes?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.items')}</Label>
              {poForm.items?.map((item, idx) => (
                <div key={idx} className="grid grid-cols-4 gap-2">
                  <Input placeholder={t('jobManagement.productName')} value={item.product_name || ''} onChange={e => {
                    const items = [...poForm.items];
                    items[idx] = {...items[idx], product_name: e.target.value};
                    setPOForm({...poForm, items});
                  }} />
                  <Input type="number" placeholder={t('jobManagement.qty')} value={item.quantity || ''} onChange={e => {
                    const items = [...poForm.items];
                    items[idx] = {...items[idx], quantity: parseFloat(e.target.value) || 0};
                    setPOForm({...poForm, items});
                  }} />
                  <Input type="number" placeholder={t('jobManagement.unitPrice')} value={item.unit_price || ''} onChange={e => {
                    const items = [...poForm.items];
                    items[idx] = {...items[idx], unit_price: parseFloat(e.target.value) || 0};
                    setPOForm({...poForm, items});
                  }} />
                  <Button variant="ghost" size="icon" onClick={() => {
                    const items = poForm.items.filter((_, i) => i !== idx);
                    setPOForm({...poForm, items: items.length ? items : [{}]});
                  }}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setPOForm({...poForm, items: [...(poForm.items || []), {}]})}><Plus className="h-4 w-4 mr-1" /> {t('jobManagement.addItem')}</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPODialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreatePO}>{t('jobManagement.createPO')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('jobManagement.createInvoice')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.subtotal')} *</Label>
                <Input type="number" value={invoiceForm.subtotal || ''} onChange={e => setInvoiceForm({...invoiceForm, subtotal: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.tax')}</Label>
                <Input type="number" value={invoiceForm.tax || ''} onChange={e => setInvoiceForm({...invoiceForm, tax: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.dueDate')}</Label>
              <Input type="date" value={invoiceForm.due_date || ''} onChange={e => setInvoiceForm({...invoiceForm, due_date: e.target.value})} />
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex justify-between font-bold text-lg">
                <span>{t('jobManagement.total')}</span>
                <span>{currencySymbol} {((invoiceForm.subtotal || 0) + (invoiceForm.tax || 0)).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateInvoice}>{t('jobManagement.createInvoice')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('jobManagement.recordPayment')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.paymentTypeLabel')}</Label>
                <Select value={paymentForm.payment_type || 'partial'} onValueChange={v => setPaymentForm({...paymentForm, payment_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advance">{t('jobManagement.paymentType.advance')}</SelectItem>
                    <SelectItem value="partial">{t('jobManagement.paymentType.partial')}</SelectItem>
                    <SelectItem value="final">{t('jobManagement.paymentType.final')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.amount')} *</Label>
                <Input type="number" value={paymentForm.amount || ''} onChange={e => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.paymentMethod')}</Label>
                <Select value={paymentForm.payment_method || 'bank_transfer'} onValueChange={v => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">{t('pages.bankAccounts.bankTransfer')}</SelectItem>
                    <SelectItem value="cash">{t('pages.bankAccounts.cash')}</SelectItem>
                    <SelectItem value="check">{t('pages.bankAccounts.check')}</SelectItem>
                    <SelectItem value="card">{t('pages.bankAccounts.card')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.date')}</Label>
                <Input type="date" value={paymentForm.payment_date || ''} onChange={e => setPaymentForm({...paymentForm, payment_date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.reference')}</Label>
              <Input value={paymentForm.reference || ''} onChange={e => setPaymentForm({...paymentForm, reference: e.target.value})} placeholder="TRF-123456" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleRecordPayment}>{t('jobManagement.recordPayment')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site Dialog */}
      <Dialog open={showSiteDialog} onOpenChange={setShowSiteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('jobManagement.addSiteToProject')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('jobManagement.client')} *</Label>
              <Select value={siteForm.client_id || ''} onValueChange={v => setSiteForm({...siteForm, client_id: v})}>
                <SelectTrigger><SelectValue placeholder={t('jobManagement.selectClient')} /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.siteName')} *</Label>
              <Input value={siteForm.name || ''} onChange={e => setSiteForm({...siteForm, name: e.target.value})} placeholder={isRTL ? "اسم الموقع" : "e.g., Floor 1 - Server Room"} />
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.description')}</Label>
              <Textarea value={siteForm.description || ''} onChange={e => setSiteForm({...siteForm, description: e.target.value})} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.address')}</Label>
                <Input value={siteForm.address || ''} onChange={e => setSiteForm({...siteForm, address: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.city')}</Label>
                <Input value={siteForm.city || ''} onChange={e => setSiteForm({...siteForm, city: e.target.value})} placeholder="Riyadh" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobManagement.contactPerson')}</Label>
                <Input value={siteForm.contact_person || ''} onChange={e => setSiteForm({...siteForm, contact_person: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('jobManagement.contactPhone')}</Label>
                <Input value={siteForm.contact_phone || ''} onChange={e => setSiteForm({...siteForm, contact_phone: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('jobManagement.contactEmail')}</Label>
              <Input type="email" value={siteForm.contact_email || ''} onChange={e => setSiteForm({...siteForm, contact_email: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSiteDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveSite}>{t('jobManagement.addSite')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetail;
